import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Clock3 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { apiGet, apiPost } from '@/lib/api'
import { useAuth } from '@/auth/auth-provider'
import type { AttendanceTodayResponse } from '@/types/api'

export function DailyClockInPrompt() {
  const { access, hasPermission } = useAuth()
  const queryClient = useQueryClient()
  const enabled = Boolean(access) && hasPermission('attendance.own.manage')
  const today = useQuery({
    queryKey: ['attendance', 'today', access?.id],
    queryFn: () => apiGet<AttendanceTodayResponse>('/attendance/today'),
    enabled,
    refetchOnWindowFocus: true,
  })

  const clockIn = useMutation({
    mutationFn: () => apiPost('/attendance/clock-in'),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['attendance'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      ])
    },
  })

  if (!enabled || today.isLoading || today.error || today.data?.clockedIn) return null

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/65 px-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-[2rem] border border-white/70 bg-background p-7 shadow-2xl">
        <div className="flex size-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
          <Clock3 className="size-7" />
        </div>
        <p className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-primary">Daily attendance</p>
        <h2 className="mt-2 text-2xl font-extrabold">Clock in for today</h2>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">
          Welcome back, {access?.fullName}. Your daily work session starts when you clock in.
        </p>
        <div className="mt-5 rounded-xl border bg-muted/30 p-4 text-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Today</p>
          <p className="mt-1 font-semibold">{today.data?.date ?? 'Current work day'}</p>
        </div>
        {clockIn.error ? (
          <p className="mt-4 text-sm text-destructive">{clockIn.error.message}</p>
        ) : null}
        <Button
          className="mt-6 w-full"
          size="lg"
          disabled={clockIn.isPending}
          onClick={() => clockIn.mutate()}
        >
          <Clock3 className="size-4" />
          {clockIn.isPending ? 'Clocking in...' : 'Clock in now'}
        </Button>
        <p className="mt-3 text-center text-xs text-muted-foreground">
          Your clock-in time is recorded automatically when you continue.
        </p>
      </div>
    </div>
  )
}
